<script setup lang="ts">
import { type Severity, type Size, type ButtonBehaviour, severitySwitch, getSizeSet } from '../../composables/useButtonStyles';
import { computed, toRefs, useAttrs, type PropType } from 'vue';
import { RouterLink, type RouteLocationRaw } from 'vue-router';

const props = defineProps({
  icon: {
    type: String,
    default: ''
  },
  label: {
    type: String,
    default: ''
  },
  labelClass: {
    type: String,
    default: ''
  },
  iconPos: {
    type: String as () => 'left' | 'right',
    default: 'left',
  },
  severity: {
    type: String as () => Severity,
    default: 'primary',
  },
  size: {
    type: String as () => Size,
    default: 'sm',
  },
  variant: {
    type: String as () => 'default' | 'outlined' | 'link' | 'text',
    default: 'default',
  },
  linkTo: {
    type: [Object, String] as PropType<RouteLocationRaw | string | null>,
    default: null,
  },
  href: {
    type: String,
    default: null,
  },
  passive: {
    type: Boolean,
    default: false,
  },
  behaviour: {
    type: String as () => ButtonBehaviour,
    default: 'static',
  },
  justify: {
    type: String as () => 'center' | 'start' | 'end' | 'between',
    default: 'center'
  },
});

const { icon, iconPos, label, severity, size, variant, linkTo, href, passive, justify, labelClass } = toRefs(props);
const attrs = useAttrs();

const severityClasses = computed(() => severitySwitch(severity.value));
const sizeClasses = computed(() => {
  const sizeSet = getSizeSet(size.value, props.behaviour);
  const isLabel = label?.value !== undefined && label?.value !== null && label?.value !== '';
  return {
    btn: isLabel && sizeSet.label,
    sizing: isLabel ? sizeSet.sizing : sizeSet.iconOnly,
    icon: sizeSet.icon,
  };
});

const btnDisabled = computed(() => 'disabled' in attrs);

const buttonClasses = computed(() => {
  const classes = [
    `justify-${justify.value}`,
    !passive.value && !btnDisabled.value ? 'active:scale-90' : null,
    'rounded-sm items-center no-underline transition-all',
    variant.value === 'default' && severityClasses.value.bg,
    sizeClasses.value.btn,
    variant.value !== 'link' && variant.value !== 'text' ? sizeClasses.value.sizing : null,
    variant.value === 'default' && !btnDisabled.value ? severityClasses.value.hover[0] : null,
    variant.value === 'outlined' && !btnDisabled.value ? severityClasses.value.hover[2] : null,
    variant.value === 'link' && !btnDisabled.value ? 'group' : null,
    variant.value === 'text' && !btnDisabled.value
      ? severityClasses.value.hover[1]
      : null,
    btnDisabled.value ? 'disabled:opacity-60' : 'cursor-pointer',
  ].filter(Boolean);

  if (variant.value === 'outlined') {
    classes.push(severityClasses.value.border, 'border-2');
  }

  if (variant.value !== 'default') {
    classes.push(severityClasses.value.textSecondary);
  } else {
    classes.push(severityClasses.value.text);
  }

  return classes;
});

const linkComponent = RouterLink;

const componentType = computed(() => {
  if (linkTo.value) return linkComponent;
  else if (href.value) return 'a';
  return 'button';
});

const componentProps = computed(() => {
  if (linkTo.value) return { to: linkTo.value };
  else if (href.value) return { href: href.value };
  return {};
});

const defaultedComponentProps = computed(() => {
  const isButton = componentType.value === 'button';
  const hasType = 'type' in attrs;

  return {
    ...componentProps.value,
    ...(isButton && !hasType ? { type: 'button' } : {}),
    ...attrs,
  };
});
</script>

<template>
  <Component
    :is="componentType"
    v-bind="defaultedComponentProps"
    class="mui-btn"
    :class="buttonClasses"
  >
    <template v-if="iconPos === 'left'">
      <slot name="icon">
        <i
          v-if="icon !== ''"
          :class="[icon, sizeClasses.icon]"
        />
      </slot>
    </template>

    <span
      v-if="label !== ''"
      :class="[{'group-hover:underline': variant === 'link'}, labelClass]"
    >
      {{ label }}
    </span>

    <template v-if="iconPos === 'right'">
      <slot name="icon">
        <i
          v-if="icon !== ''"
          :class="[icon, sizeClasses.icon]"
        />
      </slot>
    </template>
    <slot />
  </Component>
</template>
